import { TattooData, CategoryData } from '../types';
import * as fabric from 'fabric';

export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
  }
}

export class ConsistencyError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ConsistencyError';
  }
}

// Phase 2: Enhanced validation interfaces
export interface TattooStateValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  coordinateCheck: boolean;
  boundsCheck: boolean;
  scaleCheck: boolean;
}

export interface CanvasStateValidation {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  tattooCount: number;
  backgroundImagePresent: boolean;
  canvasDimensions: { width: number; height: number };
  duplicateIds: string[];
}

export const validateCategory = (category: any): void => {
  if (!category.id || typeof category.id !== 'string') {
    throw new ValidationError(`Invalid category ID: ${JSON.stringify(category)}`);
  }
  if (!category.name || typeof category.name !== 'string') {
    throw new ValidationError(`Invalid category name: ${JSON.stringify(category)}`);
  }
  // Ensure ID follows kebab-case format
  if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(category.id)) {
    throw new ValidationError(`Category ID must be in kebab-case: ${category.id}`);
  }
};

export const validateTattoo = (tattoo: any, validCategoryIds: Set<string>): void => {
  if (!tattoo.id || typeof tattoo.id !== 'number') {
    throw new ValidationError(`Invalid tattoo ID: ${JSON.stringify(tattoo)}`);
  }  if (!tattoo.image || typeof tattoo.image !== 'string') {
    throw new ValidationError(`Invalid tattoo image: ${JSON.stringify(tattoo)}`);
  }  if (!tattoo.categoryId || typeof tattoo.categoryId !== 'string') {
    throw new ValidationError(`Invalid tattoo categoryId: ${JSON.stringify(tattoo)}`);
  }
  // Ensure image follows naming convention: lowercase, only -, _, . allowed, must end with .svg or .png
  if (!/^[a-z0-9][a-z0-9-_\.]*\.(svg|png)$/.test(tattoo.image)) {
    throw new ValidationError(`Invalid tattoo image name. Must be lowercase, can contain only letters, numbers, -, _, and . and must end with .svg or .png: ${tattoo.image}`);
  }
  // Ensure category exists
  if (!validCategoryIds.has(tattoo.categoryId)) {
    throw new ValidationError(`Tattoo references non-existent category: ${tattoo.categoryId}`);
  }
};

export const validateData = (categoriesData: any, tattoosData: any): void => {
  // Validate categories structure
  if (!Array.isArray(categoriesData?.categories)) {
    throw new ValidationError('Categories data must have a categories array');
  }

  // Validate each category and collect valid IDs
  const validCategoryIds = new Set<string>();
  categoriesData.categories.forEach((category: any) => {
    validateCategory(category);
    validCategoryIds.add(category.id);
  });

  // Validate tattoos structure
  if (!Array.isArray(tattoosData?.tattoos)) {
    throw new ValidationError('Tattoos data must have a tattoos array');
  }

  // Validate each tattoo
  tattoosData.tattoos.forEach((tattoo: any) => {
    validateTattoo(tattoo, validCategoryIds);
  });

  // Check for duplicate IDs
  const categoryIds = new Set();
  const tattooIds = new Set();

  categoriesData.categories.forEach((category: any) => {
    if (categoryIds.has(category.id)) {
      throw new ValidationError(`Duplicate category ID found: ${category.id}`);
    }
    categoryIds.add(category.id);
  });

  tattoosData.tattoos.forEach((tattoo: any) => {
    if (tattooIds.has(tattoo.id)) {
      throw new ValidationError(`Duplicate tattoo ID found: ${tattoo.id}`);
    }
    tattooIds.add(tattoo.id);  });
};

// Phase 2: Enhanced validation functions

/**
 * Validate tattoo state for positioning and properties
 */
export const validateTattooState = (
  tattooObject: fabric.Object,
  canvasWidth: number,
  canvasHeight: number,
  tattooId: string
): TattooStateValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Coordinate validation
  const left = tattooObject.left ?? 0;
  const top = tattooObject.top ?? 0;
  const scaleX = tattooObject.scaleX ?? 1;
  const scaleY = tattooObject.scaleY ?? 1;
  const angle = tattooObject.angle ?? 0;
  
  let coordinateCheck = true;
  let boundsCheck = true;
  let scaleCheck = true;
  
  // Check coordinate validity
  if (!isFinite(left) || !isFinite(top)) {
    errors.push(`Invalid coordinates for tattoo ${tattooId}: left=${left}, top=${top}`);
    coordinateCheck = false;
  }
  
  // Check if tattoo is within canvas bounds
  const tattooWidth = (tattooObject.width ?? 100) * scaleX;
  const tattooHeight = (tattooObject.height ?? 100) * scaleY;
  
  if (left - tattooWidth/2 < 0 || left + tattooWidth/2 > canvasWidth ||
      top - tattooHeight/2 < 0 || top + tattooHeight/2 > canvasHeight) {
    warnings.push(`Tattoo ${tattooId} may be positioned outside canvas bounds`);
    boundsCheck = false;
  }
  
  // Check scale validity
  if (!isFinite(scaleX) || !isFinite(scaleY) || scaleX <= 0 || scaleY <= 0) {
    errors.push(`Invalid scale for tattoo ${tattooId}: scaleX=${scaleX}, scaleY=${scaleY}`);
    scaleCheck = false;
  }
  
  // Check for extremely small or large scales
  if (scaleX < 0.01 || scaleY < 0.01) {
    warnings.push(`Tattoo ${tattooId} has very small scale (may be invisible)`);
  }
  if (scaleX > 10 || scaleY > 10) {
    warnings.push(`Tattoo ${tattooId} has very large scale (may affect performance)`);
  }
  
  // Check angle validity
  if (!isFinite(angle)) {
    errors.push(`Invalid angle for tattoo ${tattooId}: ${angle}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    coordinateCheck,
    boundsCheck,
    scaleCheck,
  };
};

/**
 * Validate overall canvas state and consistency
 */
export const validateCanvasState = (
  canvas: fabric.Canvas,
  canvasTattoos: any[],
  expectedBackgroundImage: boolean = true
): CanvasStateValidation => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  const canvasObjects = canvas.getObjects();
  const backgroundImagePresent = canvasObjects.length > 0 && 
    canvasObjects[0].type === 'image';
  
  // Check for background image
  if (expectedBackgroundImage && !backgroundImagePresent) {
    errors.push('Expected background image not found');
  }
  
  // Count tattoos on canvas vs tracking array
  const tattooObjectsOnCanvas = canvasObjects.filter(obj => 
    obj.type !== 'image' && (obj as any).__tattooId
  ).length;
  
  if (tattooObjectsOnCanvas !== canvasTattoos.length) {
    errors.push(
      `Inconsistent tattoo count: ${tattooObjectsOnCanvas} on canvas, ${canvasTattoos.length} in tracking array`
    );
  }
  
  // Check for duplicate tattoo IDs
  const tattooIds = new Set<string>();
  const duplicateIds: string[] = [];
  
  canvasTattoos.forEach(tattoo => {
    if (tattoo.tattooId) {
      if (tattooIds.has(tattoo.tattooId)) {
        duplicateIds.push(tattoo.tattooId);
      }
      tattooIds.add(tattoo.tattooId);
    }
  });
  
  if (duplicateIds.length > 0) {
    errors.push(`Duplicate tattoo IDs found: ${duplicateIds.join(', ')}`);
  }
  
  // Check canvas dimensions
  const canvasWidth = canvas.width ?? 0;
  const canvasHeight = canvas.height ?? 0;
  
  if (canvasWidth <= 0 || canvasHeight <= 0) {
    errors.push(`Invalid canvas dimensions: ${canvasWidth}x${canvasHeight}`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    tattooCount: canvasTattoos.length,
    backgroundImagePresent,
    canvasDimensions: { width: canvasWidth, height: canvasHeight },
    duplicateIds,
  };
};

/**
 * Validate undo/redo stack consistency
 */
export const validateHistoryStack = (
  undoStack: any[],
  redoStack: any[],
  currentCanvasTattoos: any[]
): { isValid: boolean; errors: string[]; warnings: string[] } => {
  const errors: string[] = [];
  const warnings: string[] = [];
  
  // Check for corrupted stack entries
  const validateStackEntry = (entry: any, stackName: string, index: number) => {
    if (!entry.tattooId || typeof entry.tattooId !== 'string') {
      errors.push(`${stackName}[${index}]: Missing or invalid tattooId`);
    }
    
    if (entry.operation && !['add', 'delete', 'modify'].includes(entry.operation)) {
      errors.push(`${stackName}[${index}]: Invalid operation "${entry.operation}"`);
    }
    
    const requiredCoords = ['left', 'top', 'scaleX', 'scaleY', 'angle'];
    requiredCoords.forEach(coord => {
      if (typeof entry[coord] !== 'number' || !isFinite(entry[coord])) {
        errors.push(`${stackName}[${index}]: Invalid ${coord} value`);
      }
    });
  };
  
  undoStack.forEach((entry, index) => validateStackEntry(entry, 'undoStack', index));
  redoStack.forEach((entry, index) => validateStackEntry(entry, 'redoStack', index));
  
  // Check for excessive stack size
  if (undoStack.length > 100) {
    warnings.push(`Undo stack is very large (${undoStack.length} entries) - consider limiting size`);
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
  };
};

/**
 * Comprehensive validation of entire editor state
 */
export const validateEditorState = (
  canvas: fabric.Canvas,
  canvasTattoos: any[],
  undoStack: any[],
  redoStack: any[],
  hasPhoto: boolean
): {
  isValid: boolean;
  canvasValidation: CanvasStateValidation;
  historyValidation: ReturnType<typeof validateHistoryStack>;
  tattooValidations: TattooStateValidation[];
  summary: { errors: string[]; warnings: string[] };
} => {
  const canvasValidation = validateCanvasState(canvas, canvasTattoos, hasPhoto);
  const historyValidation = validateHistoryStack(undoStack, redoStack, canvasTattoos);
  
  const tattooValidations: TattooStateValidation[] = [];
  const canvasWidth = canvas.width ?? 0;
  const canvasHeight = canvas.height ?? 0;
  
  canvasTattoos.forEach(tattoo => {
    if (tattoo.fabricObject && tattoo.tattooId) {
      const validation = validateTattooState(
        tattoo.fabricObject,
        canvasWidth,
        canvasHeight,
        tattoo.tattooId
      );
      tattooValidations.push(validation);
    }
  });
  
  // Collect all errors and warnings
  const allErrors = [
    ...canvasValidation.errors,
    ...historyValidation.errors,
    ...tattooValidations.flatMap(v => v.errors),
  ];
  
  const allWarnings = [
    ...canvasValidation.warnings,
    ...historyValidation.warnings,
    ...tattooValidations.flatMap(v => v.warnings),
  ];
  
  return {
    isValid: allErrors.length === 0,
    canvasValidation,
    historyValidation,
    tattooValidations,
    summary: {
      errors: allErrors,
      warnings: allWarnings,
    },
  };
};